/**
 * Stock Analysis Modal Component
 * Shows detailed analysis of a stock including AI-powered insights
 */
import React from 'react';
import DOMPurify from 'dompurify';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { BarChart3, Loader2, Sparkles, IndianRupee } from 'lucide-react';
import { formatValue } from './utils';

const StockAnalysisModal = ({
  isOpen,
  onClose,
  selectedStock,
  stockAnalysis,
  aiAnalysis,
  loadingAnalysis,
  loadingAI,
  onGetAIAnalysis
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <BarChart3 className="text-primary" size={28} />
            {selectedStock} Analysis
          </DialogTitle>
        </DialogHeader>

        {loadingAnalysis ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-primary" size={48} />
          </div>
        ) : stockAnalysis ? (
          <div className="space-y-6">
            {/* Company Info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="font-bold text-lg">{stockAnalysis.name}</h3>
              <div className="flex flex-wrap gap-3 mt-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {stockAnalysis.sector}
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  {stockAnalysis.industry}
                </span>
              </div>
              {stockAnalysis.description && (
                <p className="text-sm text-gray-600 mt-3 line-clamp-3">
                  {stockAnalysis.description}
                </p>
              )}
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Current Price</p>
                <p className="text-xl font-bold text-primary">₹{stockAnalysis.current_price?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase">YTD Change</p>
                <p className={`text-xl font-bold ${stockAnalysis.year_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stockAnalysis.year_change_percent >= 0 ? '+' : ''}{stockAnalysis.year_change_percent?.toFixed(2)}%
                </p>
              </div>
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase">52W High</p>
                <p className="text-xl font-bold text-green-600">₹{stockAnalysis.high_52w?.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-white border rounded-lg">
                <p className="text-xs text-gray-500 uppercase">52W Low</p>
                <p className="text-xl font-bold text-red-600">₹{stockAnalysis.low_52w?.toFixed(2)}</p>
              </div>
            </div>

            {/* Financial Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">P/E Ratio</p>
                <p className="text-lg font-semibold">{stockAnalysis.pe_ratio?.toFixed(2) || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">P/B Ratio</p>
                <p className="text-lg font-semibold">{stockAnalysis.pb_ratio?.toFixed(2) || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Dividend Yield</p>
                <p className="text-lg font-semibold">{stockAnalysis.dividend_yield?.toFixed(2) || 0}%</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">ROE</p>
                <p className="text-lg font-semibold">{stockAnalysis.roe?.toFixed(2) || 0}%</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">EPS</p>
                <p className="text-lg font-semibold">₹{stockAnalysis.eps?.toFixed(2) || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Debt/Equity</p>
                <p className="text-lg font-semibold">{stockAnalysis.debt_to_equity?.toFixed(2) || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Book Value</p>
                <p className="text-lg font-semibold">₹{stockAnalysis.book_value?.toFixed(2) || 'N/A'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase">Beta</p>
                <p className="text-lg font-semibold">{stockAnalysis.beta?.toFixed(2) || 'N/A'}</p>
              </div>
            </div>

            {/* User's Holdings */}
            <div className="p-4 bg-gradient-to-r from-primary/10 to-green-50 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <IndianRupee size={18} /> Your Holdings
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Quantity</p>
                  <p className="text-lg font-bold">{stockAnalysis.holdings?.total_quantity?.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Current Value</p>
                  <p className="text-lg font-bold text-primary">{formatValue(stockAnalysis.holdings?.total_value || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Avg Buy Price</p>
                  <p className="text-lg font-bold">₹{stockAnalysis.holdings?.avg_purchase_price?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Unrealized Gain</p>
                  <p className={`text-lg font-bold ${stockAnalysis.holdings?.total_gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatValue(stockAnalysis.holdings?.total_gain || 0)}
                    <span className="text-sm ml-1">({stockAnalysis.holdings?.gain_percent?.toFixed(2)}%)</span>
                  </p>
                </div>
              </div>
            </div>

            {/* AI Analysis Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Sparkles className="text-purple-600" size={20} />
                  AI-Powered Analysis
                </h4>
                <Button 
                  onClick={onGetAIAnalysis}
                  disabled={loadingAI}
                  className="btn-primary flex items-center gap-2"
                >
                  {loadingAI ? (
                    <>
                      <Loader2 className="animate-spin" size={16} /> Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} /> Get AI Analysis
                    </>
                  )}
                </Button>
              </div>

              {aiAnalysis ? (
                <div className="p-4 bg-purple-50 rounded-lg prose prose-sm max-w-none">
                  <div 
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(
                        aiAnalysis
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/^### (.*$)/gm, '<h3 class="text-lg font-bold mt-4 mb-2">$1</h3>')
                          .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold mt-4 mb-2">$1</h2>')
                          .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
                          .replace(/^- (.*$)/gm, '<li class="ml-4">$1</li>')
                          .replace(/\n/g, '<br/>'),
                        { ALLOWED_TAGS: ['strong','em','b','i','u','h1','h2','h3','h4','h5','h6','p','br','li','ul','ol','span','div','code','pre'], ALLOWED_ATTR: ['class'] }
                      )
                    }} 
                  />
                </div>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                  <Sparkles className="mx-auto mb-2 text-gray-400" size={32} />
                  <p>Click &quot;Get AI Analysis&quot; to generate personalized insights and recommendations for this stock.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No analysis data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StockAnalysisModal;
